/**
 * Base HTML Email Template — Trientes Design System
 * Based on Figma: Trientes-Email---Push (node 86:5377)
 *
 * Структура: Header → Image → Body (title/description/button/additional) → Footer
 * Width: 600px, Background: #302b38, Button: #d94a29, Links: #ffaf7e
 *
 * Placeholders use %%param%% syntax (same as notifications service).
 *
 * v56 (block constructor): структура шаблона — `EmailBlocks` (4 редактируемых блока,
 * каждый с собственным `enabled` toggle + кастомным CSS override). Старый
 * `EmailTemplateConfig` сохранён для backward compat — конвертируется в blocks
 * через `configToBlocks()`.
 */

// ============================================================================
// Assets
// ============================================================================

const DEFAULT_ASSETS_BASE = 'https://trientes.com/img/email-assets';

/** Генерирует объект ассетов по базовому URL проекта. */
export function getAssets(baseUrl: string = DEFAULT_ASSETS_BASE) {
  return {
    logo: `${baseUrl}/Bull logo.png`,
    emailIcon: `${baseUrl}/email-icon.svg`,
    appStore: `${baseUrl}/appstore-badge.svg`,
    googlePlay: `${baseUrl}/googleplay-badge.svg`,
    iconWelcome: `${baseUrl}/a_37.png`,
    iconSuccess: `${baseUrl}/07-l.png`,
    iconReject: `${baseUrl}/a_27.png`,
    iconAlert: `${baseUrl}/a_28.png`,
    iconBlocked: `${baseUrl}/a_34.png`,
    iconCard: `${baseUrl}/a_11.png`,
    iconLoading: `${baseUrl}/a_5.png`,
  };
}

const ASSETS = getAssets();

/**
 * Список доступных картинок для Image block dropdown'а в редакторе.
 * Phase 2 — добавим upload в S3 (bucket trientes, префикс email-assets/).
 */
export interface ImageOption {
  key: string;
  label: string;
  /** Относительный suffix (после assetsBaseUrl), URL формируется в редакторе. */
  suffix: string;
}

export const EMAIL_IMAGE_OPTIONS: ImageOption[] = [
  { key: 'welcome', label: 'Welcome (приветствие)', suffix: '/a_37.png' },
  { key: 'success', label: 'Success (зелёная галочка)', suffix: '/07-l.png' },
  { key: 'reject', label: 'Reject (отказ)', suffix: '/a_27.png' },
  { key: 'alert', label: 'Alert (security)', suffix: '/a_28.png' },
  { key: 'blocked', label: 'Blocked (блокировка)', suffix: '/a_34.png' },
  { key: 'card', label: 'Card (карта)', suffix: '/a_11.png' },
  { key: 'loading', label: 'Loading (часы / в работе)', suffix: '/a_5.png' },
];

// ============================================================================
// Block types (v56 constructor)
// ============================================================================

export interface HeaderBlock {
  enabled: boolean;
  /** URL логотипа (40x40, рекомендуется PNG с прозрачностью или SVG). */
  logoUrl: string;
  /** Текст бренда справа от логотипа. */
  brandName: string;
  /** Показывать ли email-иконку справа в шапке. */
  showEmailIcon: boolean;
  emailIconUrl: string;
  backgroundColor: string;
  textColor: string;
  /** Опциональный inline-CSS на outer cell блока (override default styling). */
  customCss?: string;
}

export interface ImageBlock {
  enabled: boolean;
  imageUrl: string;
  altText: string;
  /** Ширина картинки в px. */
  width: number;
  /** Высота картинки в px. */
  height: number;
  /** Фон рамки вокруг картинки (для эффекта «иконка в плашке»). Пусто = без рамки. */
  frameBackground: string;
  frameRadius: number;
  imageRadius: number;
  customCss?: string;
}

export interface BodyBlock {
  enabled: boolean;
  title: string;
  titleColor: string;
  titleSize: number;
  description: string;
  descriptionColor: string;
  descriptionSize: number;
  /** Опциональная inline-ссылка внутри description: при наличии текст linkText заменяется на <a>. */
  linkText?: string;
  linkUrl?: string;
  linkColor: string;
  button: {
    enabled: boolean;
    text: string;
    url: string;
    background: string;
    textColor: string;
    borderRadius: number;
  };
  additionalText?: string;
  additionalColor: string;
  customCss?: string;
}

export interface FooterBlock {
  enabled: boolean;
  /** Многострочный юридический текст (\n становится <br/>). */
  legalInfo: string;
  legalColor: string;
  showAppBadges: boolean;
  appStoreUrl: string;
  googlePlayUrl: string;
  appStoreImageUrl: string;
  googlePlayImageUrl: string;
  copyrightText: string;
  /** Год для строки "Copyright © YYYY". */
  year: string;
  customCss?: string;
}

export interface EmailBlocks {
  header: HeaderBlock;
  image: ImageBlock;
  body: BodyBlock;
  footer: FooterBlock;
}

// ============================================================================
// Defaults
// ============================================================================

const DEFAULT_LEGAL_INFO = `Trientes is a trademark of Trust Change Sp. z o.o.
Trust Change Sp. z o.o. is a limited liability company registered in Poland.
KRS: 0001090716 | NIP: 7011191958 | REGON: 527919235
Registered Office: ul. Hoża 86, lok. 210, 00-682 Warsaw, Poland
RDWW Registration Number: RDWW-1241 (Virtual Currency Business Register)
RDWW Registration Date: April 3, 2024

Authorized activities include:
• Exchange between virtual currencies and fiat currencies
• Exchange between virtual currencies
• Intermediation in such exchanges
• Maintenance of virtual currency accounts in accordance with Polish law`;

/** Дефолтная структура блоков (вкл. все 4 блока, дефолтная иконка — welcome). */
export function defaultEmailBlocks(assetsBaseUrl?: string): EmailBlocks {
  const a = assetsBaseUrl ? getAssets(assetsBaseUrl) : ASSETS;
  return {
    header: {
      enabled: true,
      logoUrl: a.logo,
      brandName: 'Trientes',
      showEmailIcon: true,
      emailIconUrl: a.emailIcon,
      backgroundColor: '#302b38',
      textColor: '#ffffff',
    },
    image: {
      enabled: true,
      imageUrl: a.iconWelcome,
      altText: '',
      width: 240,
      height: 240,
      frameBackground: 'rgba(113,108,108,0.12)',
      frameRadius: 48,
      imageRadius: 16,
    },
    body: {
      enabled: true,
      title: 'Welcome to Trientes!',
      titleColor: '#ffffff',
      titleSize: 28,
      description: 'Your account has been created successfully.',
      descriptionColor: '#ffffff',
      descriptionSize: 16,
      linkColor: '#ffaf7e',
      button: {
        enabled: true,
        text: 'Get Started',
        url: '%%app_url%%',
        background: '#d94a29',
        textColor: '#ffffff',
        // v60.4 (2026-06-01): radius = height/2 = 28 для идеального pill
        // по SVG макету Tamplate.svg (rect 177×56 rx=28). Раньше 48 —
        // выглядел как rounded rectangle, не pill.
        borderRadius: 28,
      },
      additionalColor: '#ffffff',
    },
    footer: {
      enabled: true,
      legalInfo: DEFAULT_LEGAL_INFO,
      legalColor: 'rgba(255,255,255,0.50)',
      showAppBadges: true,
      appStoreUrl: 'https://apps.apple.com/app/trientes',
      googlePlayUrl: 'https://play.google.com/store/apps/details?id=com.trientes',
      appStoreImageUrl: a.appStore,
      googlePlayImageUrl: a.googlePlay,
      copyrightText: '© Trust Change Sp. z o.o. All rights reserved.',
      year: String(new Date().getFullYear()),
    },
  };
}

// ============================================================================
// Renderer (blocks → HTML)
// ============================================================================

function escapeNewlines(s: string): string {
  return s.replace(/\n/g, '<br/>');
}

function renderHeaderHTML(b: HeaderBlock): string {
  if (!b.enabled) return '';
  const emailIcon = b.showEmailIcon
    ? `<td align="right" width="40">
        <table cellpadding="0" cellspacing="0">
          <!-- v60.4: email-icon — radius=20 для идеального круга (SVG Tamplate.svg). -->
          <tr><td style="background: rgba(113,108,108,0.12); border-radius: 20px; width: 40px; height: 40px; text-align: center; line-height: 40px;">
            <img src="${b.emailIconUrl}" width="24" height="24" alt="" style="display: inline-block; vertical-align: middle;" />
          </td></tr>
        </table>
      </td>`
    : '';
  return `<tr><td style="padding: 28px; background-color: ${b.backgroundColor};${b.customCss || ''}">
    <table width="100%" cellpadding="0" cellspacing="0">
      <tr>
        <td style="font-family: 'SF UI Display', Arial, sans-serif; font-size: 28px; font-weight: 600; color: ${b.textColor}; line-height: 34px;">
          <img src="${b.logoUrl}" width="40" height="40" alt="${b.brandName}" style="display: inline-block; vertical-align: middle; margin-right: 16px; border-radius: 50%;" />
          ${b.brandName}
        </td>
        ${emailIcon}
      </tr>
    </table>
  </td></tr>`;
}

function renderImageHTML(b: ImageBlock): string {
  if (!b.enabled || !b.imageUrl) return '';
  const hasFrame = b.frameBackground && b.frameBackground !== 'transparent';
  if (hasFrame) {
    return `<tr><td style="height: 24px;"></td></tr>
      <tr><td align="center" style="padding: 0 28px;${b.customCss || ''}">
        <table width="544" cellpadding="0" cellspacing="0" style="background: ${b.frameBackground}; border-radius: ${b.frameRadius}px; height: ${b.height + 10}px;">
          <tr><td align="center" style="padding: 8px;">
            <img src="${b.imageUrl}" width="${b.width}" height="${b.height}" alt="${b.altText}" style="display: block; border-radius: ${b.imageRadius}px;" />
          </td></tr>
        </table>
      </td></tr>`;
  }
  return `<tr><td style="height: 24px;"></td></tr>
    <tr><td align="center" style="padding: 0 28px;${b.customCss || ''}">
      <img src="${b.imageUrl}" width="${b.width}" height="${b.height}" alt="${b.altText}" style="display: block; border-radius: ${b.imageRadius}px;" />
    </td></tr>`;
}

function renderBodyHTML(b: BodyBlock): string {
  if (!b.enabled) return '';
  const descriptionHtml = b.linkText && b.linkUrl
    ? b.description.replace(
        b.linkText,
        `<a href="${b.linkUrl}" style="color: ${b.linkColor}; text-decoration: none;">${b.linkText}</a>`,
      )
    : b.description;
  const buttonHTML = b.button.enabled && b.button.text
    ? `<tr><td align="center" style="padding: 0;">
        <table cellpadding="0" cellspacing="0">
          <tr><td align="center" style="background: ${b.button.background}; border-radius: ${b.button.borderRadius}px; padding: 16px 64px; height: 56px;">
            <a href="${b.button.url || '#'}" style="color: ${b.button.textColor}; font-family: 'SF UI Display', Arial, sans-serif; font-size: 16px; font-weight: 600; text-decoration: none; display: inline-block;">
              ${b.button.text}
            </a>
          </td></tr>
        </table>
      </td></tr>`
    : '';
  const additionalHTML = b.additionalText
    ? `<tr><td style="height: 24px;"></td></tr>
      <tr><td align="center" style="font-family: 'SF UI Display', Arial, sans-serif; font-size: 16px; font-weight: 400; line-height: 24px; color: ${b.additionalColor}; text-align: center;">
        ${b.additionalText}
      </td></tr>`
    : '';
  return `<tr><td style="height: 24px;"></td></tr>
    <tr><td style="padding: 16px 28px 0 28px;${b.customCss || ''}">
      <table width="100%" cellpadding="0" cellspacing="0">
        <tr><td align="center" style="font-family: 'SF UI Display', Arial, sans-serif; font-size: ${b.titleSize}px; font-weight: 600; color: ${b.titleColor}; line-height: ${Math.round(b.titleSize * 1.21)}px; text-align: center;">
          ${b.title}
        </td></tr>
        <tr><td style="height: 24px;"></td></tr>
        <tr><td align="center" style="font-family: 'SF UI Display', Arial, sans-serif; font-size: ${b.descriptionSize}px; font-weight: 400; line-height: ${Math.round(b.descriptionSize * 1.5)}px; color: ${b.descriptionColor}; text-align: center;">
          ${descriptionHtml}
        </td></tr>
        ${b.button.enabled && b.button.text ? '<tr><td style="height: 24px;"></td></tr>' : ''}
        ${buttonHTML}
        ${additionalHTML}
      </table>
    </td></tr>`;
}

function renderFooterHTML(b: FooterBlock): string {
  if (!b.enabled) return '';
  const appBadges = b.showAppBadges
    ? `<tr><td style="height: 24px;"></td></tr>
      <tr><td align="center">
        <a href="${b.appStoreUrl}" style="text-decoration: none; margin-right: 12px;">
          <img src="${b.appStoreImageUrl}" width="120" height="40" alt="App Store" style="display: inline-block;" />
        </a>
        <a href="${b.googlePlayUrl}" style="text-decoration: none;">
          <img src="${b.googlePlayImageUrl}" width="135" height="40" alt="Google Play" style="display: inline-block;" />
        </a>
      </td></tr>`
    : '';
  // v60.4: убран border-top — по запросу пользователя (горизонтальная линия
  // между body и footer выглядела чужеродно поверх однотонного #302B38).
  return `<tr><td style="padding: 24px 28px 24px 28px;${b.customCss || ''}">
    <table width="100%" cellpadding="0" cellspacing="0">
      <tr><td align="center" style="font-family: Arial, sans-serif; font-size: 12px; font-weight: 400; line-height: 120%; color: ${b.legalColor}; text-align: center;">
        ${escapeNewlines(b.legalInfo)}
      </td></tr>
      ${appBadges}
      <tr><td style="height: 24px;"></td></tr>
      <tr><td align="center" style="font-family: Arial, sans-serif; font-size: 12px; font-weight: 400; line-height: 120%; color: ${b.legalColor}; text-align: center;">
        ${b.copyrightText}
      </td></tr>
      <tr><td align="center" style="font-family: Arial, sans-serif; font-size: 12px; font-weight: 400; line-height: 120%; color: ${b.legalColor}; text-align: center; padding-top: 8px;">
        Copyright © ${b.year}
      </td></tr>
    </table>
  </td></tr>`;
}

/** Главный рендерер blocks → полный email HTML. */
export function renderBlocks(blocks: EmailBlocks): string {
  const title = blocks.body.enabled ? blocks.body.title : '';
  return `<!DOCTYPE html>
<html lang="en" xmlns="http://www.w3.org/1999/xhtml">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <title>${title}</title>
  <!--[if mso]><noscript><xml><o:OfficeDocumentSettings><o:PixelsPerInch>96</o:PixelsPerInch></o:OfficeDocumentSettings></xml></noscript><![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: #1a1a2e; font-family: Arial, sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #1a1a2e;">
    <tr><td align="center" style="padding: 20px 0;">
      <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background-color: ${blocks.header.enabled ? blocks.header.backgroundColor : '#302b38'}; border-radius: 0;">
        ${renderHeaderHTML(blocks.header)}
        ${renderImageHTML(blocks.image)}
        ${renderBodyHTML(blocks.body)}
        ${renderFooterHTML(blocks.footer)}
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

// ============================================================================
// Legacy config (backward compat) — конвертация в blocks
// ============================================================================

export interface EmailTemplateConfig {
  iconUrl?: string;
  title: string;
  description: string;
  buttonText?: string;
  buttonUrl?: string;
  additionalText?: string;
  linkText?: string;
  linkUrl?: string;
}

/** Конвертирует старый формат EmailTemplateConfig → блочную структуру. */
export function configToBlocks(config: EmailTemplateConfig, assetsBaseUrl?: string): EmailBlocks {
  const blocks = defaultEmailBlocks(assetsBaseUrl);
  if (config.iconUrl) {
    blocks.image.imageUrl = config.iconUrl;
  } else {
    blocks.image.enabled = false;
  }
  blocks.body.title = config.title;
  blocks.body.description = config.description;
  blocks.body.linkText = config.linkText;
  blocks.body.linkUrl = config.linkUrl;
  if (config.buttonText) {
    blocks.body.button.enabled = true;
    blocks.body.button.text = config.buttonText;
    blocks.body.button.url = config.buttonUrl || '#';
  } else {
    blocks.body.button.enabled = false;
  }
  blocks.body.additionalText = config.additionalText;
  return blocks;
}

/**
 * Legacy renderer — генерирует HTML из старого config-формата.
 * Внутри делегирует через configToBlocks → renderBlocks.
 */
export function renderEmailTemplate(config: EmailTemplateConfig): string {
  return renderBlocks(configToBlocks(config));
}

// ============================================================================
// Presets (для quick-start из dropdown'а)
// ============================================================================

/** Генерирует пресеты email-шаблонов с ассетами проекта (legacy формат). */
export function getEmailPresets(assetsBaseUrl?: string): Record<string, EmailTemplateConfig> {
  const a = assetsBaseUrl ? getAssets(assetsBaseUrl) : ASSETS;
  return {
    welcome: {
      iconUrl: a.iconWelcome,
      title: 'Welcome to Trientes!',
      description: 'Your account has been created successfully. Start exploring the platform and discover all available features.',
      buttonText: 'Get Started',
      buttonUrl: '%%app_url%%',
      additionalText: 'If you have any questions, feel free to contact our support team.',
    },
    kyc_approved: {
      iconUrl: a.iconSuccess,
      title: 'KYC Verified',
      description: 'Your identity verification has been approved. All platform features are now unlocked.',
      buttonText: 'Open Dashboard',
      buttonUrl: '%%app_url%%/dashboard',
    },
    kyc_rejected: {
      iconUrl: a.iconReject,
      title: 'Verification Issue',
      description: 'We were unable to verify your identity. Please review the requirements and try again.',
      buttonText: 'Retry Verification',
      buttonUrl: '%%app_url%%/kyc',
      additionalText: 'Reason: %%reason%%',
    },
    deposit: {
      iconUrl: a.iconSuccess,
      title: 'Deposit Received',
      description: 'Your account has been credited with %%amount%% %%currency%%.',
      buttonText: 'View Balance',
      buttonUrl: '%%app_url%%/accounts',
    },
    withdrawal: {
      iconUrl: a.iconSuccess,
      title: 'Withdrawal Processed',
      description: 'Your withdrawal of %%amount%% %%currency%% has been processed successfully.',
      buttonText: 'View Transaction',
      buttonUrl: '%%app_url%%/transactions/%%trx_num%%',
      additionalText: 'Transaction ID: %%trx_num%%',
    },
    card_issued: {
      iconUrl: a.iconCard,
      title: 'Card Issued',
      description: 'Your virtual card ending in %%card_last4%% is ready to use.',
      buttonText: 'View Card',
      buttonUrl: '%%app_url%%/cards',
    },
    security_alert: {
      iconUrl: a.iconAlert,
      title: 'Security Alert',
      description: 'A new login was detected on your account from %%device%% at %%login_time%%.',
      buttonText: 'Review Activity',
      buttonUrl: '%%app_url%%/security',
      additionalText: 'If this was not you, please change your password immediately.',
    },
    password_reset: {
      iconUrl: a.iconLoading,
      title: 'Password Reset',
      description: 'We received a request to reset your password. Click the button below to create a new password.',
      buttonText: 'Reset Password',
      buttonUrl: '%%reset_url%%',
      additionalText: 'This link will expire in %%duration%%. If you did not request this, please ignore this email.',
    },
    transaction_alert: {
      iconUrl: a.iconLoading,
      title: 'Transaction Alert',
      description: 'A transaction of %%amount%% %%currency%% was made on your account.',
      buttonText: 'View Details',
      buttonUrl: '%%app_url%%/transactions/%%trx_num%%',
      additionalText: 'Fee: %%fee%% %%currency%%',
    },
    account_blocked: {
      iconUrl: a.iconBlocked,
      title: 'Account Restricted',
      description: 'Your account has been temporarily restricted.',
      buttonText: 'Contact Support',
      buttonUrl: '%%app_url%%/support',
      additionalText: 'Reason: %%reason%%',
    },
  };
}

export const EMAIL_PRESETS: Record<string, EmailTemplateConfig> = getEmailPresets();

/** @deprecated Используйте getEmailPresets(assetsBaseUrl) для мультипроектности */
export const LEGACY_EMAIL_PRESETS = EMAIL_PRESETS;
